/**
 * Component Placement Utilities for Auto-Container System
 *
 * Provides intelligent positioning and container creation logic
 * to prevent component overlap when adding components via slash commands.
 */

import { Component, ComponentType, Position, Size } from "@/types";
import { v4 as uuidv4 } from "uuid";

/**
 * Calculate the next available position for a new container
 *
 * Strategy:
 * - If no components exist, return top-left starting position
 * - If no containers exist (real or pending), return top-left starting position
 * - Otherwise, find the bottom-most container (considering both real and pending) and position below it
 */
export function findNextContainerPosition(
  components: Component[],
  pendingPositions?: Map<string, { position: Position; size: Size; timestamp: number }>
): Position {
  // Account for canvas wrapper padding (8px on each side)
  const CANVAS_WRAPPER_PADDING = 8;
  const STARTING_X = CANVAS_WRAPPER_PADDING;
  const STARTING_Y = CANVAS_WRAPPER_PADDING;
  const VERTICAL_SPACING = 0; // No spacing between containers

  // Find all top-level containers (no parentId)
  const topLevelContainers = components.filter(
    (c) =>
      !c.parentId &&
      (c.type === ComponentType.FLEX_CONTAINER ||
        c.type === ComponentType.CONTAINER)
  );

  // Convert pending positions to virtual containers for calculation
  const pendingContainers: Array<{ position: Position; size?: Size }> = [];
  if (pendingPositions && pendingPositions.size > 0) {
    pendingPositions.forEach((data) => {
      pendingContainers.push({
        position: data.position,
        size: data.size, // Use actual container size from pending data
      });
    });
  }

  // If no real or pending containers exist, start at top-left
  if (topLevelContainers.length === 0 && pendingContainers.length === 0) {
    return { x: STARTING_X, y: STARTING_Y, z: 1 };
  }

  // Helper function to calculate bottom position
  const calculateBottom = (pos: Position, size?: Size): number => {
    const height = typeof size?.height === 'number'
      ? size.height + 100 // Add buffer for multiple padding layers, borders, and margins
      : 450; // Default for auto-height containers
    return pos.y + height;
  };

  // Find the bottom-most position among real containers
  let maxBottom = 0;
  let bottomMostPosition = { x: STARTING_X, y: STARTING_Y };

  topLevelContainers.forEach((container) => {
    const bottom = calculateBottom(container.position, container.size);
    if (bottom > maxBottom) {
      maxBottom = bottom;
      bottomMostPosition = container.position;
    }
  });

  // Also consider pending containers
  pendingContainers.forEach((pending) => {
    const bottom = calculateBottom(pending.position, pending.size);
    if (bottom > maxBottom) {
      maxBottom = bottom;
      bottomMostPosition = pending.position;
    }
  });

  // Calculate height for positioning (use default if we only have pending)
  const bottomMostHeight = maxBottom > 0 ? maxBottom - bottomMostPosition.y : 450;

  const newPosition = {
    x: CANVAS_WRAPPER_PADDING, // Account for canvas wrapper padding
    y: bottomMostPosition.y + bottomMostHeight + VERTICAL_SPACING,
    z: 1,
  };

  console.log('📍 [CONTAINER_POSITION] Calculated next position:', {
    realContainers: topLevelContainers.length,
    pendingContainers: pendingContainers.length,
    bottomMostPosition,
    bottomMostHeight,
    maxBottom,
    newPosition,
    calculation: `${bottomMostPosition.y} + ${bottomMostHeight} + ${VERTICAL_SPACING} = ${newPosition.y}`
  });

  return newPosition;
}

/**
 * Create a flex container with default settings
 */
export function createAutoContainer(
  position: Position,
  projectId: string,
  pageId: string,
  canvasWidth?: number,
  contentHeight?: number // Optional: height of the content being added
): Partial<Component> {
  // Full viewport width minus canvas wrapper padding
  // With box-sizing: border-box, borders are included in width, not added to it
  const CANVAS_WRAPPER_PADDING = 8;
  const calculatedWidth = canvasWidth
    ? canvasWidth - (CANVAS_WRAPPER_PADDING * 2)
    : 1920 - (CANVAS_WRAPPER_PADDING * 2);

  // Calculate container minimum height based on content
  // For small components (buttons, inputs), use compact min-height
  // For large components (charts), use tall min-height
  const PADDING = 16; // 1rem padding on top and bottom = 32px total
  const COMPACT_MIN_HEIGHT = 80; // For buttons, inputs, etc.
  const DEFAULT_MIN_HEIGHT = 450; // For charts and other large content

  const calculatedMinHeight = contentHeight
    ? Math.max(contentHeight + (PADDING * 2), COMPACT_MIN_HEIGHT) // Content + padding, minimum compact height
    : DEFAULT_MIN_HEIGHT; // Default to tall for charts and unknown content

  // Debug logging
  console.log('📦 [AUTO-CONTAINER] Creating container with smart sizing:', {
    contentHeight,
    calculatedMinHeight,
    padding: PADDING * 2,
    isCompact: calculatedMinHeight <= COMPACT_MIN_HEIGHT,
  });

  const container: Partial<Component> = {
    id: uuidv4(),
    type: ComponentType.FLEX_CONTAINER,
    name: `auto-container-${Date.now()}`,
    properties: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'flex-start', // Changed from 'stretch' to prevent children filling height
      gap: '1rem',
      border: '1px solid #e5e7eb', // Border in properties so FlexContainer uses it
    },
    position: position,
    size: { width: calculatedWidth, height: calculatedMinHeight }, // Use numeric height (CSS auto handled in style)
    style: {
      padding: '1rem',
      display: 'flex',
      // width removed - using explicit size.width instead
      minHeight: `${calculatedMinHeight}px`, // Minimum height based on initial content
      height: 'auto', // Allow container to grow with content
    },
    projectId,
    pageId,
    children: [], // Will be populated with child component IDs
    locked: false,
    hidden: false,
  };

  return container;
}

/**
 * Check if a component should be auto-wrapped in a container
 *
 * Returns the selected container if one exists and is a container type,
 * otherwise returns null (indicating a new container should be created)
 */
export function getTargetContainer(
  selectedComponentIds: string[],
  components: Component[]
): Component | null {
  if (selectedComponentIds.length === 0) {
    return null;
  }

  const componentMap = new Map(components.map((component) => [component.id, component]));

  const isContainer = (component: Component | undefined) =>
    !!component &&
    (component.type === ComponentType.FLEX_CONTAINER || component.type === ComponentType.CONTAINER);

  for (const selectedId of selectedComponentIds) {
    let current = componentMap.get(selectedId);

    while (current) {
      if (isContainer(current)) {
        return current;
      }

      if (!current.parentId) {
        break;
      }

      current = componentMap.get(current.parentId);
    }
  }

  return null;
}
